import { useState, useEffect } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Camera } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { MvpLockedPopup } from "@/shared/components/mvp";
import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";
import { getProcessingStatusColor, getProcessingStatusLabel } from "@/features/assets/services/statusColors";

interface AssetCardProps {
  asset: Asset;
  index: number;
  onClick: () => void;
  isSelected?: boolean;
}

export function AssetCard({ asset, index, onClick, isSelected = false }: AssetCardProps) {
  const [imageError, setImageError] = useState(false);
  const [showMvpLocked, setShowMvpLocked] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [asset.id, asset.photos[0]?.file_path]);

  const firstPhoto = asset.photos[0];

  return (
    <>
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md border-l-4",
        isSelected
          ? "bg-primary/10 border-primary shadow-md"
          : "hover:bg-accent/30 border-primary/50 hover:border-primary"
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <span className="text-sm text-muted-foreground font-medium min-w-[20px]">
              {String.fromCharCode(65 + index)}.
            </span>

            {firstPhoto?.file_path && !imageError ? (
              <img
                src={firstPhoto.file_path}
                alt={asset.name}
                className="w-10 h-10 rounded object-cover border border-border"
                onError={(e) => {
                  console.warn('[AssetCard] Image failed to load:', {
                    assetName: asset.name,
                    assetId: asset.id,
                    filePath: firstPhoto.file_path,
                    error: e
                  });
                  setImageError(true);
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded bg-muted flex items-center justify-center border border-border">
                <Camera className="h-5 w-5 text-muted-foreground" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{asset.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {asset.manufacturer && `💼 ${asset.manufacturer}`}
                {asset.model_number && ` • Model Number: ${asset.model_number}`}
                {asset.model_name && ` • Model Name: ${asset.model_name}`}
                {asset.serial_number && ` • ${asset.serial_number}`}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 ml-2">
            {asset.processing_status && asset.processing_status !== 'COMPLETE' && (
              <Badge className={cn("text-[10px] px-1.5 py-0 h-5", getProcessingStatusColor(asset.processing_status))}>
                {getProcessingStatusLabel(asset.processing_status)}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
          {asset.photos.length > 0 && (
            <span className="flex items-center gap-1">
              📸 {asset.photos.length} photo{asset.photos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
    <MvpLockedPopup
      open={showMvpLocked}
      onOpenChange={setShowMvpLocked}
    />
    </>
  );
}
